import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Button, LanguageSwitch, Logo, getProduct, productQueryOptions, useSession } from '@quoter/web-core'
import { FOODS } from '@/lib/foods'
import { useI18n } from '@/hooks/useI18n'
import { FoodTable } from '@/components/FoodTable'

/**
 * The food table for anyone, no account needed — the page the landing links
 * to and the one people share. It gives the table away on purpose: what LowFodmaps
 * sells is the guided reintroduction and the diary, and this is where someone
 * finds out they want those.
 */
export function PublicTable() {
  const { t, fmt } = useI18n()
  const session = useSession()
  const trialDays = useQuery(productQueryOptions).data?.trial_days ?? getProduct().trialDays
  const signedIn = session.status === 'authenticated'

  return (
    <main className="min-h-screen bg-canvas">
      <header className="mx-auto flex h-16 w-full max-w-3xl items-center justify-between px-4">
        <Link to={signedIn ? '/' : '/table'}>
          <Logo />
        </Link>
        <div className="flex items-center gap-4">
          <LanguageSwitch />
          <Link
            to={signedIn ? '/' : '/login'}
            className="text-[13px] font-semibold text-gray-600 underline-offset-2 hover:text-gray-900 hover:underline"
          >
            {signedIn ? t.nav.today : t.publicTable.signIn}
          </Link>
        </div>
      </header>

      <div className="mx-auto w-full max-w-3xl space-y-8 px-4 pb-20 pt-6">
        <div>
          <p className="text-[12px] font-bold uppercase tracking-[0.1em] text-brand-700">{t.publicTable.kicker}</p>
          <h1 className="mt-2 font-display text-[40px] font-normal leading-[1.1] tracking-tightish text-gray-900 sm:text-[48px]">
            {t.publicTable.title}
          </h1>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-gray-600">
            {fmt(t.publicTable.lede, { count: FOODS.length })}
          </p>
        </div>

        <FoodTable />

        {!signedIn && (
          <section className="rounded-xl bg-ink px-6 py-8 text-gray-50 sm:px-9">
            <h2 className="max-w-[24ch] font-display text-[26px] font-normal leading-tight tracking-tightish">
              {t.publicTable.ctaTitle}
            </h2>
            <p className="mt-3 max-w-xl text-[14.5px] leading-relaxed text-gray-300">{t.publicTable.ctaBody}</p>
            <Link to="/pricing" className="mt-6 inline-block">
              <Button size="lg" className="bg-brand-500 text-gray-50 hover:bg-brand-600 active:bg-brand-700">
                {fmt(t.publicTable.cta, { n: trialDays })}
              </Button>
            </Link>
          </section>
        )}
      </div>
    </main>
  )
}
